import {
  Box, Button, FormControl, MenuItem, Select, SelectChangeEvent, SvgIcon, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, useTheme,
} from '@mui/material';
import { TransferDetail } from '../../../app/services/api/generated';

import RoundAndFormatValue from '../../../components/shared/RoundAndFormatValue';
import { useTranslation } from 'react-i18next';
import { TransferItemRow } from '../interfaces/StockTransferDetailsTypes';
import { Copy01 } from '../../../assets';
import { TableHeader } from '../../../components/forms/TableHeader';
import { Pill } from '../../../components/shared/Pill';
import { Section } from '../../../components/ui/Section';

const StockTransferDetailTable = (props: {
  transferDetails: TransferDetail[] | undefined,
  isDeleted: boolean,
  acceptedDate: string | undefined,
  transferType: string,
  transferItemRows: TransferItemRow[],
  handleUomChange: (id: number) => (event: SelectChangeEvent) => void,
  handleQuantityChange: (id: number) => (event: React.ChangeEvent<HTMLInputElement>) => void,
  handleCopyQtysButtonClick: () => void,
  currencyCode?: string
}) => {

  const { t } = useTranslation('stockTransfersDetails');
  const theme = useTheme();

  const BodyTableCell = (props: { children?: React.ReactNode, align?: 'center' | 'left' }) => {
    return (<TableCell
      sx={{
        textAlign: props.align ?? 'left',
      }}
    >
      {props.children}
    </TableCell>);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', pb: 5 }}>
        {!props.isDeleted && !props.acceptedDate && props.transferType === 'Incoming' && <Button
          variant="primary"
          size='lg'
          startIcon={<SvgIcon><Copy01 /></SvgIcon>}
          onClick={props.handleCopyQtysButtonClick}
        >{t('copyQuantitiesButton')}
        </Button>}
      </Box>
      <Section>
        <TableHeader>
          Items
          {(props.transferDetails?.length ?? 0) > 0 && <Pill text={`${props.transferDetails?.length} Items`}></Pill>}
        </TableHeader>
        <TableContainer sx={{ borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}
                  align="left">
                  {t('table.itemNumber')}
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}
                  align="left">
                  {t('table.itemDescription')}
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}
                  align="left">
                  {t('table.uom')}
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}
                  align="left">
                  {t('table.requestedQuantity')}
                </TableCell>
                {!props.isDeleted && !props.acceptedDate && props.transferType === 'Incoming' && <TableCell sx={{ fontWeight: 'bold' }}
                  align="left">
                  {t('table.receivedUom')}
                </TableCell>}
                <TableCell sx={{ fontWeight: 'bold' }}
                  align="left">
                  {t('table.receivedQuantity')}
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}
                  align="left">
                  {t('table.requestedValue')}
                </TableCell>
                {props.acceptedDate && <TableCell sx={{ fontWeight: 'bold' }}
                  align="left">
                  {t('table.receivedValue')}
                </TableCell>}

              </TableRow>
            </TableHead>
            <TableBody>
              {props.transferDetails?.map((row, index) => (
                <TableRow
                  key={row.id?.toString() + '-' + row.itemNumber}
                >
                  <BodyTableCell>{row.itemNumber}</BodyTableCell>
                  <BodyTableCell>{row.description}</BodyTableCell>
                  <BodyTableCell>{row.primaryUom}</BodyTableCell>
                  <BodyTableCell>{row.requestedQuantity}</BodyTableCell>
                  {!props.isDeleted && !props.acceptedDate && props.transferType === 'Incoming' && <BodyTableCell>
                    <FormControl fullWidth
                      size='small'>
                      <Select
                        labelId={`uoms-label-${row.id}`}
                        id={`uoms-select-${row.id}`}
                        value={props.transferItemRows.find(x => x.id === row.id)?.selectedItemUom ?? ''}
                        sx={{ backgroundColor: theme.palette.common.white }}
                        onChange={props.handleUomChange(row.id ?? -1)}
                      >
                        <MenuItem value=''>
                          <em>{t('table.none')}</em>
                        </MenuItem>
                        {props.transferItemRows.find(x => x.id === row.id)?.possibleItemUoms?.map((uom) => {
                          return <MenuItem key={uom.code}
                            value={uom.code}>{uom.code}</MenuItem>;
                        })}
                      </Select>
                    </FormControl>
                  </BodyTableCell>}
                  <BodyTableCell>
                    {!props.isDeleted && !props.acceptedDate && props.transferType === 'Incoming' && <FormControl
                      size='small'
                      sx={{ width: '80px' }}>
                      <TextField
                        type='number'
                        size='small'
                        id={`quantity-input-${row.id}`}
                        sx={{ backgroundColor: theme.palette.common.white }}
                        onChange={props.handleQuantityChange(row.id ?? -1)}
                        value={props.transferItemRows.find(x => x.id === row.id)?.receivedQuantity ?? 0}
                      />
                    </FormControl>}
                    {(props.isDeleted || props.acceptedDate || props.transferType === 'Outgoing') && ((row.receivedQuantity !== null) ? row.receivedQuantity : '-')}
                  </BodyTableCell>
                  <BodyTableCell>
                    <RoundAndFormatValue value={row.requestedValue!}
                      currencyCode={props.currencyCode} />
                  </BodyTableCell>
                  {props.acceptedDate && <BodyTableCell>{row.receivedValue !== undefined ? <RoundAndFormatValue value={row.receivedValue}
                    currencyCode={props.currencyCode} /> : '-'}</BodyTableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Section>
    </Box>
  );
};

export default StockTransferDetailTable;